
































































































































































































import { Component, Mixins, Ref, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import SpotList, { SpotFilter } from "@/components/Spot/List.vue";
import EntryItem from "@/components/EntryItem.vue";
import SubEntryGroupingCard, {
  SubEntryGroupingFilter,
} from "@/components/Investor/SubEntryGroupingCard.vue";
import VPageContentSticky from "@/components/VPageContentSticky.vue";
import { Route } from "vue-router";
import { Brand, Company, Media, Product, Spot, Target, User } from "@/models";
import EntryMixin from "@/mixins/http/Entry";
import DatePicker, {
  DATE_FORMAT,
  DatePickerData,
} from "@/components/DatePicker.vue";
import MediaSelect from "@/components/MediaSelect.vue";
import BreakdownTable, {
  BreakdownTableFilter,
  BreakdownTotals,
} from "@/components/Investor/BreakdownTable.vue";
import BreakdownTotal from "@/components/Investor/BreakdownTotal.vue";
import MediaValue from "@/interfaces/MediaValue";
import InvestorMixin from "@/mixins/http/Investor";
import VSwitchButton from "@/components/VSwitchButton.vue";
import DaysComparationsCard from "@/components/Investor/DaysComparationsCard.vue";
import EqualityHelper from "@/services/EqualityHelper";
import QueryHelper from "@/services/QueryHelper";
import QueryParser from "@/services/QueryParser";
import {
  FEATURE_AUDITEL,
  FEATURE_CREATIVITY,
  FEATURE_CURRENCY_UNIT,
} from "@/constants";
import WithTimeIntervals from "@/models/interfaces/WithTimeIntervals";
import Entry from "@/models/Entry";
import Compressor from "@/services/Compressor";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import MultiLevelSelect from "@/components/MultiLevelSelect.vue";
import AggregationMode from "@/components/Spot/AggregationMode.vue";
import AdcontrolProfile from "@/models/AdcontrolProfile";
import ToastInvoker from "@/utils/ToastInvoker";
import VodComparationsCard from "@/components/Investor/VodComparationsCard.vue";

const requestTag = "entries";

interface DateRange {
  from: string;
  to: string;
}

interface InvestorSummaryFilter {
  unitOfMeasure: string;
  media: MediaValue;
  date: DateRange;
  agencies: boolean;
}

export interface BreakdownSummaryTotals extends BreakdownTotals {
  creativities: number;
  entries: number;
}

Component.registerHooks(["beforeRouteUpdate"]);

@Component({
  components: {
    EntryItem,
    SubEntryGroupingCard,
    VSwitchButton,
    DaysComparationsCard,
    DatePicker,
    MediaSelect,
    BreakdownTable,
    BreakdownTotal,
    VPageContentSticky,
    SpotList,
    GrpTargetSelect,
    MultiLevelSelect,
    AggregationMode,
    VodComparationsCard,
  },
})
export default class InvestorSummary extends Mixins(EntryMixin, InvestorMixin) {
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("user") readonly user!: User;
  @Getter("profile") readonly adcontrolProfile!: AdcontrolProfile;

  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    // eslint-disable-next-line
    defaultUnit?: any
  ) => string;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;

  @Ref("spotList") readonly spotList!: SpotList;

  entry: Company | Brand | Product | null = null;

  aggregationMode = false;
  selected: Spot[] = [];
  search = "";

  ready = false;
  weekdaysComparisons = [];
  hoursComparisons: (Entry & WithTimeIntervals)[] = [];
  chartEntries: Array<Product | Company | Brand> = [];
  currentGrpTarget: Target | null = null;

  breakdownTableFilter_: BreakdownTableFilter | null = null;

  spotTypeDetailSwitch = true;
  showSpotTypeSwitch = false;

  loading = {
    entry: false,
    breakdown: false,
    chartEntries: false,
    export: false,
  };

  filter: InvestorSummaryFilter = {
    date: { from: "", to: "" },
    media: { radio: true, tv: true },
    unitOfMeasure: "seconds",
    agencies: false,
  };

  totals: BreakdownSummaryTotals = {
    spots: 0,
    seconds: 0,
    broadcasters: 0,
    creativities: 0,
    agencies: 0,
    entries: 0,
    grp: 0,
    grpOverSpots: 0,
    vodBroadcasters: 0,
    ebiquityMax: 0,
    ebiquityMin: 0,
  };

  auditelReady = false;

  onSelected(selected: Spot[]) {
    this.selected = selected;
  }

  onAggregate() {
    const ids = this.selected.map((o) => o.id);
    const compressed = Compressor.compress(ids);
    this.$router.push({
      name: "InvestorCreativityDetail",
      params: { product_id: this.entry?.id, ids: compressed },
      query: {
        ...this.convertInvestorSpotFilterToQuery(
          this.getDetailFilterFromCurrentFilter()
        ),
      },
    });
  }

  get enoughSpots(): boolean {
    return this.totals.creativities > 2;
  }

  get spotTypeDetail(): boolean {
    return this.spotTypeDetailSwitch && this.showSpotTypeSwitch;
  }

  onSpotListLoaded() {
    this.showSpotTypeSwitch = !!this.spotList?.spots?.find(
      (e) => e.spotType === "SPONSORIZZAZIONI"
    );
  }

  onSpotClicked(spot: Spot) {
    const ids = [spot.id];
    const compressed = Compressor.compress(ids);
    this.$router.push({
      name: "InvestorCreativityDetail",
      params: { product_id: this.entry?.id, ids: compressed },
      query: {
        ...this.convertInvestorSpotFilterToQuery(
          this.getDetailFilterFromCurrentFilter()
        ),
      },
    });
  }

  protected convertInvestorSpotFilterToQuery(
    isp: InvestorSummaryFilter
  ): Dictionary<string> {
    return {
      from: isp.date.from as string,
      to: isp.date.to as string,
      tv: isp.media.tv ? "1" : "0",
      radio: isp.media.radio ? "1" : "0",
      unit: isp.unitOfMeasure,
    };
  }

  protected getDetailFilterFromCurrentFilter(): InvestorSummaryFilter {
    return {
      media: this.filter.media,
      date: this.filter.date,
      unitOfMeasure: this.filter.unitOfMeasure,
      agencies: false,
    };
  }

  onAuditelReady() {
    this.auditelReady = true;
  }

  parseParamsOrDefault(param: string | undefined, defaultValue: boolean) {
    return param !== undefined ? (param == "1" ? true : false) : defaultValue;
  }

  buildFilterFromQuery(): InvestorSummaryFilter {
    const query = this.$route.query;
    const { radio, tv } = query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      6,
      "days"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);

    const media = this.parseAndCleanMediaValue(
      QueryParser.queryToBoolean(radio),
      QueryParser.queryToBoolean(tv)
    );

    return {
      unitOfMeasure: (query.unit as string) ?? "spots",
      agencies: this.parseParamsOrDefault(query.agencies as string, false),
      media,
      date: {
        from,
        to,
      },
    };
  }

  @Watch("$store.state.settings.profile")
  async onProfileUpdated() {
    this.enforceProfileMediaType();
    await this.load(this.entity.type, this.entity.id);
  }

  @Watch("filter", { deep: true })
  onFilterChanged() {
    const radio_condition: boolean =
      this.filter.media.radio &&
      (this.filter.unitOfMeasure == "grp" ||
        this.filter.unitOfMeasure == "grp_over_spots" ||
        this.filter.unitOfMeasure == "audience");

    const tv_condition: boolean =
      this.filter.media.tv &&
      (this.filter.unitOfMeasure == "ebiquity_min" ||
        this.filter.unitOfMeasure == "ebiquity_max");

    if (radio_condition || tv_condition) this.filter.unitOfMeasure = "seconds";

    const isEqual = QueryHelper.isSameQuery(
      this.$route.query,
      this.queryFilter
    );
    if (!isEqual) {
      this.$router.replace({ query: { ...this.queryFilter } });
    }
  }

  get hideNonVodWhenJustVod(): boolean {
    if (this.adcontrolProfile?.mediaSourceKind == "msk_vod") {
      return true;
    }
    return false;
  }

  get queryFilter() {
    return {
      from: this.filter.date.from,
      to: this.filter.date.to,
      radio: this.filter.media.radio ? "1" : "0",
      tv: this.filter.media.tv ? "1" : "0",
      unit: this.filter.unitOfMeasure,
    };
  }

  get subEntryGroupingFilter(): SubEntryGroupingFilter {
    return {
      date: { ...this.filter.date },
      media: { ...this.filter.media },
      unitOfMeasure: this.filter.unitOfMeasure,
    };
  }

  get entity() {
    const { id, type } = this.$route.params;
    return { id, type };
  }

  get daysComparationsFilter() {
    return {
      date: { ...this.filter.date },
      media: { ...this.filter.media },
    };
  }

  get typeDescription(): string {
    const type = this.$route.params.type;

    switch (type) {
      case "companies":
        return "azienda";

      case "brands":
        return "brand";

      case "products":
        return "prodotto";
    }

    return "";
  }

  get isProduct() {
    return this.$route.params.type === "products";
  }

  get exportDisabled() {
    return (this.filter?.media?.tv || this.loading.export) ?? true;
  }

  get showSubEntryPartition() {
    return this.entry?.type === "brands" || this.entry?.type === "companies";
  }

  get showSpotsTable() {
    return this.entry?.type === "products" && this.userHasCreativityFeature();
  }

  get spotTableFilter(): SpotFilter | null {
    if (!this.entry || !this.filter?.date.from || !this.filter?.date.to) {
      return null;
    }
    const media = Media.mediaToList(this.filter.media);

    const grpTarget = this.currentGrpTarget?.id;

    return {
      dateRange: [this.filter.date.from, this.filter.date.to],
      product: this.entry.id,
      media,
      grpTarget,
      vod: this.adcontrolProfile?.mediaSourceKind === "msk_vod",
    };
  }

  get breakdownTableFilter(): BreakdownTableFilter | undefined {
    if (this.filter) {
      return {
        date: this.filter.date,
        media: this.filter.media,
        ready: this.ready,
        entry: this.entry,
        grouped: this.filter.agencies,
      };
    } else {
      return undefined;
    }
  }

  async created() {
    this.filter = this.buildFilterFromQuery();
    this.enforceProfileMediaType();

    await this.load(this.entity.type, this.entity.id);
  }

  private enforceProfileMediaType() {
    if (this.adcontrolProfile?.accountType === "radio") {
      this.filter.media.tv = false;
      this.filter.media.radio = true;
    } else if (this.adcontrolProfile?.accountType === "tv") {
      this.filter.media.tv = true;
      this.filter.media.radio = false;
    }
  }

  beforeRouteUpdate(to: Route, from: Route, next: Function) {
    if (!EqualityHelper.isEqualDeep(to.params, from.params)) {
      this.filter = this.buildFilterFromQuery();
      this.load(to.params.type, to.params.id);
    }
    next();
  }

  async load(type: string, id: string) {
    await this.loadEntry(type, id);

    if (type !== "products") {
      this.totals.creativities = 0;
    }

    if (this.filter.media.radio) {
      this.totals.grp = -10; //FIXME
      this.totals.grpOverSpots = -10; //FIXME
    }

    if (!this.user.hasFeature(FEATURE_CURRENCY_UNIT)) {
      this.totals.ebiquityMax = -10; //FIXME
      this.totals.ebiquityMin = -10; //FIXME
    }

    this.breakdownTableFilter_ = this.breakdownTableFilter || null;
    this.scrollToTop();
  }

  onSettingsChanged(settings: { unitOfMeasure?: string; grouped?: boolean }) {
    if (!this.filter) {
      return;
    }
    if (settings.unitOfMeasure !== undefined) {
      this.filter.unitOfMeasure = settings.unitOfMeasure;
    }
    if (settings.grouped !== undefined) {
      this.filter.agencies = settings.grouped;
    }
  }

  onCreativityCounter(counter: number) {
    this.totals.creativities = counter;
  }

  onSubEntryLoaded(entries: { count: number; hasSponsor: boolean }) {
    this.totals.entries = entries.count;
    this.showSpotTypeSwitch = entries.hasSponsor;
  }

  async loadEntry(type: string, id: string) {
    this.loading.entry = true;

    switch (type) {
      case "companies":
        this.entry = await this.getCompany(id, requestTag);
        break;

      case "brands":
        this.entry = await this.getBrand(id, requestTag);
        break;

      case "products":
        this.entry = await this.getProduct(id, requestTag);
        break;
    }

    this.loading.entry = false;
    this.ready = true;
  }

  async onTargetSelected(target: Target) {
    this.currentGrpTarget = target;
  }

  get grpTargetId() {
    if (this.currentGrpTarget && !this.filter.media.radio)
      return this.currentGrpTarget?.id;
    else return null;
  }

  get unitSelectorOptions(): Array<MultiLevelObject> {
    const alwaysOn = [
      { id: "seconds", text: "Secondi" },
      { id: "spots", text: "Spot" },
    ];

    if (this.user.hasEbiquity && !this.filter.media.tv) {
      return [
        ...alwaysOn,
        { id: "ebiquity_min", text: "€ (Stima Min.)" },
        { id: "ebiquity_max", text: "€ (Stima Max.)" },
      ];
    }

    if (this.hasAuditel && !this.filter.media.radio) {
      return [
        ...alwaysOn,

        { id: "grp", text: "GRP" },
        { id: "grp_over_spots", text: "GRP / Spot" },
        { id: "audience", text: "Contatti" },
      ];
    } else {
      return alwaysOn;
    }
  }

  protected onDateSelected(data: DatePickerData) {
    if (this.filter && data.range) {
      Vue.set(this.filter.date, "from", data.range.from);
      Vue.set(this.filter.date, "to", data.range.to);
    }
  }

  protected onBreakdownLoading() {
    this.loading.breakdown = true;
  }

  protected onBreakdownLoaded(totals: BreakdownSummaryTotals) {
    this.totals = Object.assign({}, this.totals, totals);
    this.loading.breakdown = false;
  }

  protected scrollToTop() {
    const main = document.querySelector(".root-element");
    if (main) {
      main.scrollTop = 0;
    }
  }

  protected userHasCreativityFeature(): boolean {
    return this.user.hasFeature(FEATURE_CREATIVITY) || this.user.isAgencyUser;
  }

  get hasAuditel(): boolean {
    return this.user.hasFeature(FEATURE_AUDITEL);
  }

  async onExportClick() {
    const payload = {
      filter: {
        date_range: Object.values(this.filter.date),
      },
    };
    this.loading.export = true;
    this.productTimePointsExport(
      this.entry!.id,
      payload,
      `${this.entry?.name}_punti_ora`
    )
      .then(() => {
        this.loading.export = false;
      })
      .catch(() => {
        this.loading.export = false;
        ToastInvoker.errorToast("Errore durante l'esportazione", null);
      });
  }
}
